import type { AgentRun, Doc, Issue, Project, Team } from "./types";

export type TeamAttentionReason = "overdue" | "review" | "urgent";

export interface TeamAttentionItem {
  issue: Issue;
  reasons: TeamAttentionReason[];
}

export interface TeamInsights {
  teamIssues: Issue[];
  attention: TeamAttentionItem[];
  teamProjects: Project[];
  activeProjects: Project[];
  teamDocs: Doc[];
  contextDocs: Doc[];
  scopedAgentRuns: AgentRun[];
  currentAgentRuns: AgentRun[];
}

export interface DeriveTeamInsightsInput {
  team: Team;
  issues: readonly Issue[];
  projects: readonly Project[];
  docs?: readonly Doc[];
  agentRuns?: readonly AgentRun[];
  /** Injected for deterministic rendering and tests. Defaults to Date.now(). */
  now?: number;
}

const OPEN_STATUSES = new Set<Issue["status"]>([
  "backlog",
  "todo",
  "in_progress",
  "in_review",
]);

const PROJECT_STATUS_ORDER: Record<Project["status"], number> = {
  in_progress: 0,
  planned: 1,
  paused: 2,
  backlog: 3,
  completed: 4,
  canceled: 5,
};

const PRIORITY_ORDER: Record<Issue["priority"], number> = {
  1: 0,
  2: 1,
  3: 2,
  4: 3,
  0: 4,
};

export function deriveTeamInsights({
  team,
  issues,
  projects,
  docs = [],
  agentRuns = [],
  now = Date.now(),
}: DeriveTeamInsightsInput): TeamInsights {
  const teamIssues = issues.filter(
    (issue) =>
      issue.workspaceId === team.workspaceId && issue.teamId === team.id
  );
  const teamIssueIds = new Set(teamIssues.map((issue) => issue.id));

  const teamProjects = projects.filter(
    (project) =>
      project.workspaceId === team.workspaceId &&
      project.teamIds.includes(team.id)
  );
  const teamProjectIds = new Set(teamProjects.map((project) => project.id));

  const teamDocs = docs
    .filter(
      (doc) =>
        doc.workspaceId === team.workspaceId &&
        (doc.teamId === team.id ||
          (doc.projectId !== null && teamProjectIds.has(doc.projectId)))
    )
    .sort(compareDocs);
  const teamDocIds = new Set(teamDocs.map((doc) => doc.id));

  const attention = teamIssues
    .filter((issue) => OPEN_STATUSES.has(issue.status))
    .map((issue): TeamAttentionItem => ({
      issue,
      reasons: attentionReasons(issue, now),
    }))
    .filter((item) => item.reasons.length > 0)
    .sort((a, b) => compareAttention(a, b, now));

  const activeProjects = teamProjects
    .filter(
      (project) =>
        project.status === "in_progress" || project.status === "planned"
    )
    .sort(compareProjects);

  const memberIds = new Set(team.memberIds);
  const scopedAgentRuns = agentRuns
    .filter((run) => {
      if (run.workspaceId !== team.workspaceId) return false;

      const linkedToTeam =
        run.projectIds.some((id) => teamProjectIds.has(id)) ||
        run.touchedIssueIds.some((id) => teamIssueIds.has(id)) ||
        run.touchedDocIds.some((id) => teamDocIds.has(id));
      if (linkedToTeam) return true;

      // Older agents did not always record touched entities. A run without any
      // scope metadata can still be attributed to the team when its actor is a
      // team member. Explicit references to another team's work win over this
      // fallback and keep the activity feed honest.
      const hasExplicitScope =
        run.projectIds.length > 0 ||
        run.touchedIssueIds.length > 0 ||
        run.touchedDocIds.length > 0;
      return (
        !hasExplicitScope &&
        run.actorId !== null &&
        memberIds.has(run.actorId)
      );
    })
    .sort(compareRuns);

  return {
    teamIssues,
    attention,
    teamProjects,
    activeProjects,
    teamDocs,
    contextDocs: teamDocs.slice(0, 6),
    scopedAgentRuns,
    currentAgentRuns: scopedAgentRuns.slice(0, 6),
  };
}

export function attentionReasons(
  issue: Issue,
  now = Date.now()
): TeamAttentionReason[] {
  if (!OPEN_STATUSES.has(issue.status)) return [];

  const reasons: TeamAttentionReason[] = [];
  if (issue.dueDate !== null && issue.dueDate < startOfToday(now)) {
    reasons.push("overdue");
  }
  if (issue.status === "in_review") reasons.push("review");
  if (issue.priority === 1) reasons.push("urgent");
  return reasons;
}

function compareAttention(
  a: TeamAttentionItem,
  b: TeamAttentionItem,
  now: number
): number {
  const reasonDelta =
    attentionRank(a.reasons) - attentionRank(b.reasons);
  if (reasonDelta !== 0) return reasonDelta;

  const dueDelta =
    (a.issue.dueDate ?? Number.POSITIVE_INFINITY) -
    (b.issue.dueDate ?? Number.POSITIVE_INFINITY);
  if (dueDelta !== 0) return dueDelta;

  const priorityDelta =
    PRIORITY_ORDER[a.issue.priority] - PRIORITY_ORDER[b.issue.priority];
  if (priorityDelta !== 0) return priorityDelta;

  const freshnessDelta = b.issue.updatedAt - a.issue.updatedAt;
  if (freshnessDelta !== 0) return freshnessDelta;

  // `now` is intentionally part of the comparator contract so callers can
  // inject one stable timestamp for both classification and sorting.
  return compareRelativeAge(a.issue.createdAt, b.issue.createdAt, now);
}

function attentionRank(reasons: TeamAttentionReason[]): number {
  if (reasons.includes("overdue")) return 0;
  if (reasons.includes("review")) return 1;
  return 2;
}

function compareProjects(a: Project, b: Project): number {
  const statusDelta =
    PROJECT_STATUS_ORDER[a.status] - PROJECT_STATUS_ORDER[b.status];
  if (statusDelta !== 0) return statusDelta;

  const targetDelta =
    (a.targetDate ?? Number.POSITIVE_INFINITY) -
    (b.targetDate ?? Number.POSITIVE_INFINITY);
  if (targetDelta !== 0) return targetDelta;

  return b.updatedAt - a.updatedAt;
}

function compareDocs(a: Doc, b: Doc): number {
  if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
  return b.updatedAt - a.updatedAt;
}

function compareRuns(a: AgentRun, b: AgentRun): number {
  if (a.status !== b.status) {
    if (a.status === "in_progress") return -1;
    if (b.status === "in_progress") return 1;
  }
  return runTimestamp(b) - runTimestamp(a);
}

function runTimestamp(run: AgentRun): number {
  return run.endedAt ?? run.startedAt ?? run.createdAt;
}

function startOfToday(now: number): number {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return today.getTime();
}

function compareRelativeAge(a: number, b: number, now: number): number {
  return Math.abs(now - a) - Math.abs(now - b);
}
